import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { Accordion } from '@/modules/shared/components/accordion/Accordion'
import useFindMatchRoutes from '@/modules/shared/hooks/useFindMatchRoutes'
import { HomeIcon } from '@/modules/shared/icons/HomeIcon'
import { PurchaserIcon } from '@/modules/shared/icons/PurchaserIcon'
import { SideBarContext } from '@/modules/shared/layouts/app-layout/SideBarContext'
import SwitchOrganisationModal from '@/modules/shared/layouts/header/switch-organisation-modal/SwitchOrganisationModal'
import SideNavLink from '@/modules/shared/layouts/side-navigation/SideNavLink'
import { AccountSideBarItems } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/AccountSideBarItems'
import { AdministrationSideBarItems } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/AdministrationSideBarItems'
import { ProductSideBarItem } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/ProductSideBarItem'
import { QuotesSideBarItem } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/QuotesSideBarItems'
import { ReportsSideBarItem } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/ReportsSideBarItem'
import { UsersSideBarItem } from '@/modules/shared/layouts/side-navigation/purchaser-navigation/UsersSideBarItem'
import { CatalogsSideBarItem } from '@/modules/shared/layouts/side-navigation/supplier-navigation/CatalogsSideBarItem'
import { InvoicesSideBarItem } from '@/modules/shared/layouts/side-navigation/supplier-navigation/InvoicesSideBarItem'
import { PurchaseOrdersSideBarItem } from '@/modules/shared/layouts/side-navigation/supplier-navigation/PurchaseOrdersSideBarItem'
import { Themes } from '@/modules/shared/types/Themes'
import { classNames } from '@/modules/shared/utils/classNames'

export default function SupplierNavigation() {
  const sideBar = useContext(SideBarContext)

  const { t } = useTranslation()

  const {
    accordionDefaultKey,
    myProductRoutesMatch,
    purchaserRoutesMatch,
    usersRoutesMatch,
    catalogRoutesMatch,
    sideBarIndexKeys,
    purchaseOrdersRoutesMatch,
    invoiceRoutesMatch,
    quoteRoutesMatch,
    reportsRoutesMatch,
  } = useFindMatchRoutes()

  const [openSwitchOrg, setOpenSwitchOrg] = useState(false)
  const [openAccordionKey, setOpenAccordionKey] = useState<number | undefined>()
  const { currentOrganisation } = useSessionUser()
  const { featureFlags } = currentOrganisation || {}
  const { userPermissions } = useSessionUser()

  useEffect(() => {
    if (accordionDefaultKey) {
      setOpenAccordionKey(accordionDefaultKey)
    }
  }, [accordionDefaultKey])

  const changeOpenAccordionKey = (key: number | undefined) => {
    if (key === openAccordionKey) {
      setOpenAccordionKey(undefined)
    } else {
      setOpenAccordionKey(key)
    }
  }

  const onCloseDrawer = () => sideBar?.setDrawerOpen && sideBar.setDrawerOpen(false)

  return (
    <>
      <nav className="flex-1 pb-10 text-sm" data-testid="side-navigation-items">
        <section className="space-y-1.5">
          <NavLink
            data-testid="home-navigation-link"
            to="/dashboard"
            onClick={() => onCloseDrawer()}
            className={({ isActive }) => {
              return classNames('flex h-11 items-center gap-x-2 rounded-md px-3 transition duration-200', {
                'bg-primary text-white': isActive,
                'bg-gray-200 hover:bg-gray-300': !isActive,
              })
            }}
          >
            <HomeIcon className="size-7" />
            {t('general.home', 'Home')}
          </NavLink>
          <PurchaseOrdersSideBarItem
            purchaseOrdersRoutesMatch={purchaseOrdersRoutesMatch}
            openAccordionKey={openAccordionKey}
            changeOpenAccordionKey={changeOpenAccordionKey}
            onCloseDrawer={onCloseDrawer}
            sideBarIndexKey={sideBarIndexKeys.purchaseOrdersSideBarKey}
          />
          <InvoicesSideBarItem
            invoiceRoutesMatch={invoiceRoutesMatch}
            openAccordionKey={openAccordionKey}
            changeOpenAccordionKey={changeOpenAccordionKey}
            onCloseDrawer={onCloseDrawer}
            sideBarIndexKey={sideBarIndexKeys.invoiceSideBarKey}
          />
          {featureFlags?.supplierShowProductsTab && (
            <ProductSideBarItem
              myProductRoutesMatch={myProductRoutesMatch}
              openAccordionKey={openAccordionKey}
              changeOpenAccordionKey={changeOpenAccordionKey}
              onCloseDrawer={onCloseDrawer}
              isSupplier
              sideBarIndexKey={sideBarIndexKeys.myProductSideBarKey}
            />
          )}
          <CatalogsSideBarItem
            catalogRoutesMatch={catalogRoutesMatch}
            openAccordionKey={openAccordionKey}
            changeOpenAccordionKey={changeOpenAccordionKey}
            onCloseDrawer={onCloseDrawer}
            sideBarIndexKey={sideBarIndexKeys.catalogSideBarKey}
          />
          <QuotesSideBarItem
            quoteRoutesMatch={quoteRoutesMatch}
            openAccordionKey={openAccordionKey}
            changeOpenAccordionKey={changeOpenAccordionKey}
            onCloseDrawer={onCloseDrawer}
            sideBarIndexKey={sideBarIndexKeys.quoteSideBarKey}
          />
          {featureFlags?.supplierShowPurchasersTab && (
            <Accordion
              titlePrefixIcon={<PurchaserIcon className="size-7" />}
              title={t('general.purchasers', 'Purchasers')}
              buttonClassName="h-11 px-3"
              activeColor={purchaserRoutesMatch ? Themes.Primary : undefined}
              defaultOpen={openAccordionKey === sideBarIndexKeys.purchaserSidebarKey}
              handleTriggerClick={() => changeOpenAccordionKey(sideBarIndexKeys.purchaserSidebarKey)}
              keepActiveColor
            >
              <SideNavLink
                onCloseDrawer={() => onCloseDrawer()}
                testId="purchaser-directory-navigation"
                sideNavLinkItems={[
                  {
                    key: t('general.myPurchasers', 'My Purchasers'),
                    target: '/my-purchasers',
                  },
                  {
                    key: t('general.purchaserDirectory', 'Purchaser Directory'),
                    target: '/purchaser-directory',
                  },
                  {
                    key: t('general.purchaserTags', 'Purchaser Tags'),
                    target: '/purchaser-tags',
                  },
                ]}
              />
            </Accordion>
          )}
          {userPermissions?.SUPPLIER_CAN_MANAGE_REPORTS && (
            <ReportsSideBarItem
              reportsRoutesMatch={reportsRoutesMatch}
              openAccordionKey={openAccordionKey}
              changeOpenAccordionKey={changeOpenAccordionKey}
              onCloseDrawer={onCloseDrawer}
              sideBarIndexKey={sideBarIndexKeys.reportsSideBarKey}
            />
          )}
          {(userPermissions?.CAN_VIEW_USERS || userPermissions?.CAN_MANAGE_USERS) && (
            <UsersSideBarItem
              usersRoutesMatch={usersRoutesMatch}
              openAccordionKey={openAccordionKey}
              changeOpenAccordionKey={changeOpenAccordionKey}
              onCloseDrawer={onCloseDrawer}
              sideBarIndexKey={sideBarIndexKeys.usersSideBarKey}
            />
          )}
        </section>
        <AccountSideBarItems
          openAccordionKey={openAccordionKey}
          changeOpenAccordionKey={changeOpenAccordionKey}
          onCloseDrawer={onCloseDrawer}
        />
        <AdministrationSideBarItems onCloseDrawer={onCloseDrawer} />
      </nav>
      <SwitchOrganisationModal showModal={openSwitchOrg} setShowModal={setOpenSwitchOrg} />
    </>
  )
}
